import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { IoArrowBack } from 'react-icons/io5';
import './ConfiguracionPagosScreen.css';

function ConfiguracionPagosScreen() {
    const { userData } = useAuth();
    const [cargando, setCargando] = useState(false);
    const [statusCuenta, setStatusCuenta] = useState(null); // 'pending', 'complete', o null
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        // Verificar status al cargar (especialmente si viene del redirect de stripe)
        const checkStatus = async () => {
            if (!userData?.condominioId) return;
            const db = getFirestore();
            const docRef = doc(db, 'condominios', userData.condominioId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.stripeAccountId) {
                    setStatusCuenta(data.stripeAccountStatus || 'pending');
                }
            }
        };
        checkStatus();
    }, [userData]);

    const handleConectar = async () => {
        if (!userData?.condominioId) {
            alert("Error: No tienes un condominio asignado.");
            return;
        }

        setCargando(true);
        try {
            const createConnectFn = httpsCallable(functions, 'createStripeConnectAccount');
            const { data } = await createConnectFn({ condominioId: userData.condominioId });

            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error("No se recibió la URL de onboarding.");
            }
        } catch (error) {
            console.error(error);
            alert("Hubo un problema al iniciar la conexión: " + error.message);
        }
        setCargando(false);
    };

    const handleBack = () => {
        // Force navigation to the admin config screen
        navigate('/admin/configuracion-fiscal');
    };

    return (
        <div className="config-pagos-container" style={{
            padding: '40px',
            maxWidth: '1000px',
            margin: '0 auto',
            fontFamily: 'Inter, sans-serif'
        }}>
            {/* Header / Nav */}
            <div style={{ marginBottom: '30px' }}>
                <button
                    onClick={handleBack}
                    style={{
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontWeight: '500',
                        color: '#374151',
                        transition: 'all 0.2s',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                >
                    <IoArrowBack size={18} /> Volver a Configuración
                </button>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h1 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#111827', marginBottom: '10px' }}>Configuración de Pagos</h1>
                <p style={{ fontSize: '1.1rem', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
                    Conecta tu cuenta de Stripe para automatizar el cobro de cuotas y la dispersión de fondos a tu cuenta bancaria.
                </p>
            </div>

            {/* Status Messages */}
            {searchParams.get('success') && (
                <div style={{
                    padding: '16px',
                    backgroundColor: '#ecfdf5',
                    border: '1px solid #059669',
                    borderRadius: '12px',
                    color: '#065f46',
                    marginBottom: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <span style={{ fontSize: '1.5rem' }}>🎉</span>
                    <div>
                        <strong>¡Conexión Exitosa!</strong> Tu cuenta bancaria ha sido vinculada. Ahora estás listo para recibir pagos.
                    </div>
                </div>
            )}

            {searchParams.get('refresh') && (
                <div style={{
                    padding: '16px',
                    backgroundColor: '#fffbeb',
                    border: '1px solid #d97706',
                    borderRadius: '12px',
                    color: '#92400e',
                    marginBottom: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                    <div>
                        <strong>Proceso incompleto.</strong> No terminaste el formulario de Stripe. Puedes intentarlo de nuevo cuando gustes.
                    </div>
                </div>
            )}

            {/* Main Card */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '24px',
                padding: '40px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                textAlign: 'center',
                border: '1px solid #f3f4f6'
            }}>
                <div style={{ marginBottom: '20px' }}>
                    {/* Simplified Stripe Logo Style Text */}
                    <span style={{ fontSize: '3rem', fontWeight: 'bold', color: '#635bff', letterSpacing: '-1px' }}>stripe</span>
                </div>

                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '20px', color: '#1f2937' }}>Estado de la Conexión</h3>

                {statusCuenta ? (
                    <div style={{
                        backgroundColor: '#f9fafb',
                        padding: '20px',
                        borderRadius: '12px',
                        display: 'inline-block',
                        textAlign: 'left',
                        marginBottom: '20px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: statusCuenta === 'complete' ? '#10b981' : '#f59e0b' }}></div>
                            <strong style={{ color: '#374151' }}>
                                {statusCuenta === 'complete' ? 'Activa y Conectada' : 'Pendiente de Verificación (Modo Test)'}
                            </strong>
                        </div>
                        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.95rem' }}>
                            Tu cuenta ({userData?.condominioId}) está vinculada.
                            {statusCuenta !== 'complete' && ' En modo de prueba, "Pendiente" es funcional para recibir pagos.'}
                        </p>
                    </div>
                ) : (
                    <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                        <div style={{
                            backgroundColor: '#fee2e2',
                            color: '#991b1b',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '24px',
                            fontWeight: '500'
                        }}>
                            No hay cuenta conectada
                        </div>

                        <p style={{ color: '#4b5563', marginBottom: '30px' }}>
                            Al conectar tu cuenta, aceptas los términos de servicio de Stripe Connect para plataformas.
                        </p>

                        <button
                            onClick={handleConectar}
                            disabled={cargando}
                            style={{
                                backgroundColor: '#635bff',
                                color: 'white',
                                padding: '16px 32px',
                                border: 'none',
                                borderRadius: '50px',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                cursor: cargando ? 'not-allowed' : 'pointer',
                                width: '100%',
                                boxShadow: '0 4px 6px rgba(99, 91, 255, 0.4)',
                                transition: 'transform 0.1s',
                                opacity: cargando ? 0.7 : 1
                            }}
                        >
                            {cargando ? 'Redirigiendo a Stripe...' : 'Conectar Cuenta Bancaria'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ConfiguracionPagosScreen;
